import { gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import { formatISODateTime, parseISODate, toLocalDateTimeString } from '../utils/date';
import { FaseCategoriaTorneio } from './categoriaTorneioService';

export const CRIAR_FASE_CATEGORIA_TORNEIO = gql`
  mutation CriarFaseCategoriaTorneio($input: FaseCategoriaTorneioInput!) {
    criarFaseCategoriaTorneio(input: $input) {
      id
      nome
      dataInicio
      dataFim
      status
      ordem
      tipoGrupoChave
    }
  }
`;

export const ATUALIZAR_FASE_CATEGORIA_TORNEIO = gql`
  mutation AtualizarFaseCategoriaTorneio($id: ID!, $input: FaseCategoriaTorneioInput!) {
    atualizarFaseCategoriaTorneio(id: $id, input: $input) {
      id
      nome
      dataInicio
      dataFim
      status
      ordem
      tipoGrupoChave
    }
  }
`;

export const DELETAR_FASE_CATEGORIA_TORNEIO = gql`
  mutation DeletarFaseCategoriaTorneio($id: ID!) {
    deletarFaseCategoriaTorneio(id: $id)
  }
`;

export const REORDENAR_FASES_CATEGORIA_TORNEIO = gql`
  mutation ReordenarFases($input: [ReordenarFaseCategoriaTorneioInput!]!) {
    reordenarFasesCategoriaTorneio(input: $input) {
      id
      ordem
    }
  }
`;

export interface CriarFaseInput {
  categoriaTorneioId: string;
  nome: string;
  dataInicio: Date;
  dataFim: Date;
  status: string;
  ordem: number;
  tipoGrupoChave: string;
}

export const criarFase = async (
  input: CriarFaseInput,
): Promise<FaseCategoriaTorneio> => {
  try {
    const { data } = await apolloClient.mutate<{ criarFaseCategoriaTorneio: FaseCategoriaTorneio }>({
      mutation: CRIAR_FASE_CATEGORIA_TORNEIO,
      variables: {
        input: {
          ...input,
          dataInicio: toLocalDateTimeString(input.dataInicio),
          dataFim: toLocalDateTimeString(input.dataFim),
        },
      },
    });
    const fase = data?.criarFaseCategoriaTorneio;
    if (!fase) throw new Error('Resposta inválida');
    return {
      ...fase,
      dataInicio: toLocalDateTimeString(fase.dataInicio as unknown as string) ?? new Date(),
      dataFim: toLocalDateTimeString(fase.dataFim as unknown as string) ?? new Date(),
    } as FaseCategoriaTorneio;
  } catch (e) {
    throw new Error('Erro ao criar fase');
  }
};

export interface AtualizarFaseInput {
  categoriaTorneioId: string;
  nome?: string;
  dataInicio?: Date;
  dataFim?: Date;
  status?: string;
  ordem?: number;
  tipoGrupoChave?: string;
}

export const atualizarFase = async (
  id: string,
  input: AtualizarFaseInput,
): Promise<FaseCategoriaTorneio> => {
  try {
    const { data } = await apolloClient.mutate<{ atualizarFaseCategoriaTorneio: FaseCategoriaTorneio }>({
      mutation: ATUALIZAR_FASE_CATEGORIA_TORNEIO,
      variables: {
        id,
        input: {
          ...input,
          dataInicio: input.dataInicio ? toLocalDateTimeString(input.dataInicio) : undefined,
          dataFim: input.dataFim ? toLocalDateTimeString(input.dataFim) : undefined,
        },
      },
    });
    const fase = data?.atualizarFaseCategoriaTorneio;
    if (!fase) throw new Error('Resposta inválida');
    return {
      ...fase,
      dataInicio: toLocalDateTimeString(fase.dataInicio as unknown as string) ?? new Date(),
      dataFim: toLocalDateTimeString(fase.dataFim as unknown as string) ?? new Date(),
    } as FaseCategoriaTorneio;
  } catch (e) {
    throw new Error('Erro ao atualizar fase');
  }
};

export const deletarFase = async (id: string): Promise<boolean> => {
  try {
    const { data } = await apolloClient.mutate<{ deletarFaseCategoriaTorneio: boolean }>({
      mutation: DELETAR_FASE_CATEGORIA_TORNEIO,
      variables: { id },
    });
    return Boolean(data?.deletarFaseCategoriaTorneio);
  } catch (e) {
    throw new Error('Erro ao deletar fase');
  }
};

export interface ReordenarFaseInput {
  id: string;
  ordem: number;
}

export const reordenarFases = async (input: ReordenarFaseInput[]): Promise<void> => {
  try {
    await apolloClient.mutate({
      mutation: REORDENAR_FASES_CATEGORIA_TORNEIO,
      variables: { input },
    });
  } catch (e) {
    throw new Error('Erro ao reordenar fases');
  }
};
